import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

// CUBBO-2182: shows the orders still pending packing in the current SIP tote
// (cross-SKU), so the operator knows what's left and can scan the next physical
// product to continue — instead of dead-ending. Data comes from
// `GET /picking-orders/:id/sip-tote-pending` (platform PR #3605).
//
// The product rows are behind a collapsible "Ver productos" toggle (collapsed by
// default): a tote can hold dozens or hundreds of single-item orders (seen up to
// 288), so a flat list would push the scan input and the rest of the page out of
// view. The count and the scan prompt stay always visible.
//
// `orders` prop:
//   - null/undefined → list not resolved yet (loading) → render nothing
//   - []             → tote fully packed → "all packed" message
//   - [ ... ]        → render both counts, the collapsible rows + scan prompt
//
// Rows under review are listed with a badge and counted apart: they are not packable,
// but their pieces are still in the tote, so dropping them from the header would leave
// no default-visible trace of the box the operator will not be able to empty.
export const SipTotePendingList = ({ orders }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const inReviewCount = (orders ?? []).filter((order) => order.in_review).length

  useEffect(() => {
    if (inReviewCount > 0) setExpanded(true)
  }, [inReviewCount])

  if (orders == null) return null

  if (orders.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
        {t('generate_shipment.view.tote_all_packed')}
      </div>
    )
  }

  const packableCount = orders.length - inReviewCount

  return (
    <div className="my-3 rounded-md border border-blue-200 bg-blue-50 p-3 text-left">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold text-blue-800">
          {t('generate_shipment.view.tote_pending_title', { count: packableCount })}
          {inReviewCount > 0 && (
            <span className="text-amber-800">
              {' · '}
              {t('generate_shipment.view.tote_pending_in_review_count', { count: inReviewCount })}
            </span>
          )}
        </p>
        <button
          type="button"
          aria-expanded={expanded}
          // Keep the handheld scanner's focus on the product input: prevent the
          // button from stealing focus on (mouse/touch) press; toggle on click.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setExpanded((value) => !value)}
          className="shrink-0 text-sm font-medium text-blue-700 underline hover:text-blue-900"
        >
          {expanded
            ? t('generate_shipment.view.tote_pending_hide')
            : t('generate_shipment.view.tote_pending_show')}
        </button>
      </div>

      {expanded && (
        <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto pr-1">
          {orders.map((order) => (
            <li key={order.picking_order_id} className="text-sm text-gray-700">
              <span className="font-medium">#{order.order_number}</span>
              <span className="text-gray-400"> · </span>
              <span>{order.sku}</span>
              <span className="text-gray-400"> · </span>
              <span>{order.product_name}</span>
              {order.in_review && (
                <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800">
                  {t('generate_shipment.view.tote_pending_in_review_badge')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-2 text-sm font-medium text-blue-800">
        {t('generate_shipment.view.tote_pending_scan_prompt')}
      </p>
    </div>
  )
}
